/**
 * SortableTable - compact generic table with clickable sort headers and an
 * optional text filter box, plus a CSV export helper and a StatDetail line
 * (stat text + a small button opening the same table in a dialog with export).
 *
 * Built for the map pages' state-detail panels and stat drill-downs; dense
 * 12px styling matches the existing hand-rolled tables there and on the Ops
 * Data Opportunities page.
 */

import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  ArrowSortDownRegular,
  ArrowSortUpRegular,
  OpenRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { COLORS } from '../tokens/colors';

// =============================================================================
// Types
// =============================================================================

export interface SortableColumn<T> {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /** Render numbers in the monospace numeric style. */
  mono?: boolean;
  /**
   * CSS width for the column. When ANY column declares one the table switches
   * to a fixed layout, so several tables rendered with the same columns (one
   * per group, say) line up instead of each sizing to its own content; the
   * columns without a width share whatever is left.
   */
  width?: string;
  /** Comparable value when this column drives the sort. */
  sortValue: (row: T) => string | number;
  render: (row: T) => React.ReactNode;
  /** Raw value for CSV export and text filtering; defaults to sortValue. */
  csvValue?: (row: T) => string | number | undefined;
}

export interface SortableTableProps<T> {
  columns: SortableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Show a text filter box above the table (matches across all columns). */
  searchable?: boolean;
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  onRowEnter?: (row: T) => void;
  onRowLeave?: (row: T) => void;
  rowBackground?: (row: T) => string | undefined;
}

// =============================================================================
// CSV export
// =============================================================================

/**
 * Excel and Sheets evaluate any cell that opens with a formula character, so a
 * broker or initiative literally named "=cmd..." would execute on open.
 * Prefixing an apostrophe forces the cell to text (OWASP CSV injection).
 */
function neutralizeFormula(cell: string): string {
  return /^[=+\-@\t\r]/.test(cell) ? `'${cell}` : cell;
}

function csvQuote(cell: string): string {
  const safe = neutralizeFormula(cell);
  if (/[",\n\r]/.test(safe)) return `"${safe.replace(/"/g, '""')}"`;
  return safe;
}

/**
 * Filename-safe slug, for stamping export provenance (which filters were
 * active) into the download name.
 */
export function slugForFilename(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'all';
}

/** RFC 4180 CSV text from column defs + rows. Pure, so it can be unit tested. */
export function buildColumnsCsv<T>(columns: SortableColumn<T>[], rows: T[]): string {
  const header = columns.map((c) => csvQuote(c.label)).join(',');
  const body = rows.map((r) =>
    columns
      .map((c) => {
        const v = (c.csvValue ?? c.sortValue)(r);
        return csvQuote(v === undefined || v === null ? '' : String(v));
      })
      .join(','),
  );
  return [header, ...body].join('\r\n');
}

/** RFC 4180 CSV from column defs + rows, downloaded with a UTF-8 BOM. */
export function exportColumnsCsv<T>(
  filename: string,
  columns: SortableColumn<T>[],
  rows: T[],
): void {
  const csv = buildColumnsCsv(columns, rows);
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =============================================================================
// SortableTable
// =============================================================================

const useStyles = makeStyles({
  // A class rather than an inline style because :hover needs a stylesheet.
  // Gentle Haze is the palette's own hover tint. A caller's rowBackground is
  // inline and so still wins on the rows it marks, which is the point of it.
  row: {
    ':hover': { backgroundColor: COLORS.surfaceHover },
  },
});

export function SortableTable<T>(props: SortableTableProps<T>) {
  const {
    columns, rows, getRowKey, initialSort, searchable, searchPlaceholder,
    onRowClick, onRowEnter, onRowLeave, rowBackground,
  } = props;
  const styles = useStyles();

  const [sortKey, setSortKey] = useState<string | null>(initialSort?.key ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSort?.dir ?? 'asc');
  const [filter, setFilter] = useState('');

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const visibleRows = useMemo(() => {
    let out = rows;

    const needle = filter.trim().toLowerCase();
    if (needle) {
      out = out.filter((r) =>
        columns.some((c) => {
          const v = (c.csvValue ?? c.sortValue)(r);
          return v !== undefined && v !== null && String(v).toLowerCase().includes(needle);
        }),
      );
    }

    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col) {
        const mul = sortDir === 'asc' ? 1 : -1;
        out = out.slice().sort((a, b) => {
          const va = col.sortValue(a);
          const vb = col.sortValue(b);
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
          return String(va).localeCompare(String(vb)) * mul;
        });
      }
    }
    return out;
  }, [rows, columns, filter, sortKey, sortDir]);

  const fixedLayout = columns.some((c) => c.width !== undefined);

  return (
    <div>
      {searchable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Input
            size="small"
            value={filter}
            onChange={(_, data) => setFilter(data.value)}
            placeholder={searchPlaceholder ?? 'Filter...'}
            contentBefore={<SearchRegular />}
            style={{ flexGrow: 1 }}
          />
          <span style={{ fontSize: '12px', color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
            {filter.trim() ? `${visibleRows.length} of ${rows.length}` : `${rows.length} rows`}
          </span>
        </div>
      )}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '12px',
          tableLayout: fixedLayout ? 'fixed' : undefined,
        }}
      >
        {fixedLayout && (
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sortKey === c.key;
              return (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  style={{
                    textAlign: c.align ?? 'left',
                    padding: '6px 8px',
                    borderBottom: `2px solid ${COLORS.border}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    color: active ? COLORS.textPrimary : undefined,
                  }}
                >
                  {c.label}
                  {active && (
                    sortDir === 'asc'
                      ? <ArrowSortUpRegular style={{ fontSize: '12px', verticalAlign: '-2px', marginLeft: '2px' }} />
                      : <ArrowSortDownRegular style={{ fontSize: '12px', verticalAlign: '-2px', marginLeft: '2px' }} />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r) => (
            <tr
              key={getRowKey(r)}
              className={styles.row}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              onMouseEnter={onRowEnter ? () => onRowEnter(r) : undefined}
              onMouseLeave={onRowLeave ? () => onRowLeave(r) : undefined}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                backgroundColor: rowBackground?.(r),
              }}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={{
                    padding: '4px 8px',
                    borderBottom: `1px solid ${COLORS.borderSubtle}`,
                    textAlign: c.align ?? 'left',
                    fontFamily: c.mono ? "'JetBrains Mono', monospace" : undefined,
                  }}
                >
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
          {visibleRows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: '12px 8px', color: COLORS.textSecondary, textAlign: 'center' }}
              >
                No rows{filter.trim() ? ' match the filter' : ''}.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// StatDetail - a stat line with a drill-down dialog + CSV export
// =============================================================================

export interface StatDetailProps<T> {
  /** The stat text as it appears on the page, e.g. "32 states with groups". */
  text: string;
  dialogTitle: string;
  columns: SortableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Download name, e.g. "states-with-groups"; .csv appended if missing. */
  exportName: string;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
}

export function StatDetail<T>(props: StatDetailProps<T>) {
  const { text, dialogTitle, columns, rows, getRowKey, exportName, initialSort } = props;
  const [open, setOpen] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ color: COLORS.textSecondary }}>{text}</span>
      <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
        <DialogTrigger disableButtonEnhancement>
          <Tooltip content="View list" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<OpenRegular style={{ fontSize: '14px' }} />}
              aria-label={`View list: ${dialogTitle}`}
            />
          </Tooltip>
        </DialogTrigger>
        <DialogSurface style={{ maxWidth: '720px' }}>
          <DialogBody>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <SortableTable
                columns={columns}
                rows={rows}
                getRowKey={getRowKey}
                initialSort={initialSort}
                searchable
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                icon={<ArrowDownloadRegular />}
                onClick={() => exportColumnsCsv(exportName, columns, rows)}
              >
                Export CSV
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="primary">Close</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
